import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAbility } from '@casl/react';
import {
    AlertTriangle, ArrowLeft, Ban, Camera, Check, CheckCircle2, ClipboardList, Download, Expand,
    FileText, Images, Loader2, MessageSquare, Package, Paperclip, Pencil, Plus, Send,
    Trash2, Undo2, UserPlus, Users, X,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { Combobox } from '../../components/ui/combobox';
import AccessRestricted from '../../components/AccessRestricted';
import { useAuth } from '../../context/AuthContext';
import { ImageLightbox } from '../../components/ui/image-lightbox';
import { AbilityContext } from '../../context/casl/AbilityContext';
import { ACTIONS, RESOURCES } from '../../constants/permissions.constants';
import {
    DeliveryLine, DeliveryOrderDetail, DeliveryPacks, DeliveryStatus, DeliveryVehicleInput,
    Product, StockDocument, StockUserRef,
} from '../../types/stock.types';
import { Project } from '../../types/lists.types';
import { deliveriesApi, productsApi, yardsApi } from '../../utils/stock-api';
import { projectsApi } from '../../utils/lists-api';
import { ApiError } from '../../utils/api';
import { compressImage } from '../../utils/upload';
import { cn } from '../../components/ui/utils';
import { groupLinesByCategory } from '../../utils/stock-lines';
import { DeliveryStatusPill } from './DeliveryOrdersPage';

const LINE_STATUS_STYLES: Record<string, string> = {
    pending: 'text-slate-600 bg-slate-100',
    picked: 'text-emerald-700 bg-emerald-50',
    shortfall: 'text-amber-700 bg-amber-50',
    // Same red as the pick sheet uses, so the state reads the same on both screens.
    partially_picked: 'text-red-700 bg-red-50',
};

/**
 * Number of packs on the shipment (Ops, Oct 2026). Advisory counts of what went on the
 * trucks — optional, and read by nothing but the people looking at them.
 */
const PACK_FIELDS = [
    { key: 'stillages', label: 'Stillages & mixed stillages' },
    { key: 'cages', label: 'Cages' },
    { key: 'fullBoards', label: 'Full Boards' },
    { key: 'mixedBoards', label: 'Mixed Boards' },
    { key: 'empties', label: 'Empties' },
] as const;

type PackField = (typeof PACK_FIELDS)[number]['key'];

/** Server values → the editor's strings; null ("not counted") reads as an empty box. */
const packInputsFrom = (packs: DeliveryPacks | null): Record<PackField, string> | null =>
    packs == null ? null
        : Object.fromEntries(PACK_FIELDS.map((f) => [
            f.key, packs[f.key] == null ? '' : String(packs[f.key]),
        ])) as Record<PackField, string>;

/** The editor's strings → the API body; an empty box clears the count back to null. */
const packPayload = (inputs: Record<PackField, string>): Partial<DeliveryPacks> =>
    Object.fromEntries(PACK_FIELDS.map((f) => [
        f.key, inputs[f.key].trim() === '' ? null : Number(inputs[f.key]),
    ])) as Partial<DeliveryPacks>;

/** Whole numbers, zero or more — or blank, which is "not counted". */
const packInputValid = (value: string) => {
    const trimmed = value.trim();
    if (trimmed === '') return true;
    const n = Number(trimmed);
    return Number.isInteger(n) && n >= 0;
};

/**
 * Delivery Order detail — STOCK.md §4.
 *
 * Everything except the actual picking lives here: header, approval, crew, photos and
 * the completion gates. Picking has its own screen (PickSheetPage) because it is used
 * one-handed on a phone in a yard, and a page that also carries approval controls would
 * be the wrong shape for that.
 */
export default function DeliveryOrderDetailPage() {
    const ability = useAbility(AbilityContext);
    const { currentUser } = useAuth();
    const canRead = ability.can(ACTIONS.READ, RESOURCES.STOCK_DELIVERY_ORDER);
    const canAttachDocs = ability.can(ACTIONS.CREATE, RESOURCES.STOCK_PHOTO);
    const canDeleteDocs = ability.can(ACTIONS.DELETE, RESOURCES.STOCK_DOCUMENT);
    const { id, yardId } = useParams<{ id: string; yardId?: string }>();
    const navigate = useNavigate();
    const listHref = yardId ? `/stock/yards/${yardId}/deliveries` : '/stock/deliveries';
    const pickHref = yardId
        ? `/stock/yards/${yardId}/deliveries/${id}/pick`
        : `/stock/deliveries/${id}/pick`;

    const [order, setOrder] = useState<DeliveryOrderDetail | null>(null);
    const [products, setProducts] = useState<Product[]>([]);
    /** Projects for the header edit's project picker — the same list the create form reads. */
    const [projects, setProjects] = useState<Project[]>([]);
    // The pick crew is drawn from this yard's MEMBERS, not from every user in the
    // tenant (§11b): membership is what grants sight of a yard, so anyone outside it
    // cannot open the sheet they would have been assigned to.
    const [yardCrew, setYardCrew] = useState<StockUserRef[]>([]);
    const [loading, setLoading] = useState(true);
    /** Server-side refusal (yard scoping) — rendered as a page, not eternal skeletons (UAT B9). */
    const [accessDenied, setAccessDenied] = useState<string | null>(null);
    const [busy, setBusy] = useState<string | null>(null);

    // Header edit — the approving Yard Manager / Transport Manager fills in and corrects
    // the truck details (rego, driver, fleet, ship date) at any point while the run is
    // live, including once picking has started.
    const [editOpen, setEditOpen] = useState(false);
    const [editProjectId, setEditProjectId] = useState('');
    const [editFleet, setEditFleet] = useState('');
    const [editReg, setEditReg] = useState('');
    const [editDriver, setEditDriver] = useState('');
    const [editShip, setEditShip] = useState('');
    /**
     * The additional vehicles, held as the dialog's whole list and diffed server-side
     * (no id = add, id = correct, absent = remove). Only sent by a holder of
     * edit_vehicles:stock_delivery_order — the Transport Manager.
     */
    const [editVehicles, setEditVehicles] = useState<DeliveryVehicleInput[]>([]);
    /** Fleet type suggestions from meta — same list the create form offers. */
    const [fleetTypes, setFleetTypes] = useState<string[]>([]);
    /**
     * The server's photo rule: fleet type (upper-case) → required count, plus the
     * fallback for an unset or unrecognised one. Read from /meta rather than copied here,
     * so the Transport Manager sees the same number the completion gate will enforce.
     */
    const [photoRule, setPhotoRule] = useState<
        { byFleet: Record<string, number>; fallback: number }>({ byFleet: {}, fallback: 10 });

    const [productId, setProductId] = useState('');
    const [quantity, setQuantity] = useState('');
    /** The saved line being corrected in place — item and/or quantity — or null. */
    const [editLineId, setEditLineId] = useState<number | null>(null);
    const [editProductId, setEditProductId] = useState('');
    const [editQuantity, setEditQuantity] = useState('');
    const [assigneeId, setAssigneeId] = useState('');
    /** Which load photo the viewer is open on, or null when it is closed. */
    const [photoIndex, setPhotoIndex] = useState<number | null>(null);
    const docInput = useRef<HTMLInputElement | null>(null);
    /** The source document pending removal (§12.4) — confirm before it goes. */
    const [docToDelete, setDocToDelete] = useState<StockDocument | null>(null);
    /**
     * Progress across a multi-file upload, so ten photos is not ten silent seconds.
     * Compressing and sending are separate phases and each is slow enough on its own
     * to be worth naming — "Preparing 4/10" tells the user the app is working, where a
     * stalled "Uploading 0/10" reads as a hang.
     */
    const [uploading, setUploading] = useState<
        { done: number; total: number; phase: 'preparing' | 'uploading'; label: string | null } | null>(null);

    /**
     * Is this a phone or tablet? Decides camera-only vs multi-select below.
     *
     * `(pointer: coarse)` asks about the input device rather than sniffing the user agent,
     * and is evaluated once — it cannot change mid-session in any way that matters here.
     */
    const [isTouchDevice] = useState(() =>
        typeof window !== 'undefined' && window.matchMedia?.('(pointer: coarse)').matches === true);
    const [comment, setComment] = useState('');

    /**
     * The pack-count editor, held as strings so a half-typed value is preserved. Seeded
     * from the order on every load — the section has no workflow gate, so the server value
     * is the truth and a reload after a save keeps the boxes honest.
     */
    const [packInputs, setPackInputs] = useState<Record<PackField, string> | null>(null);

    const [voidOpen, setVoidOpen] = useState(false);
    const [voidReason, setVoidReason] = useState('');
    const [rejectOpen, setRejectOpen] = useState(false);
    const [rejectReason, setRejectReason] = useState('');
    const [enterOpen, setEnterOpen] = useState(false);
    const [externalRef, setExternalRef] = useState('');

    const load = useCallback(async () => {
        if (!id) return;
        try {
            setLoading(true);
            const data = await deliveriesApi.get(Number(id));
            setOrder(data);
            setPackInputs(packInputsFrom(data.packs));
            setAccessDenied(null);
        } catch (err) {
            // A 403 here is yard scoping, not a transient failure — show it on the page
            // (the server's own wording) instead of leaving skeletons up forever (UAT B9).
            // Explicitly NOT a 404: the order exists, the user may not see this yard.
            if (err instanceof ApiError && err.status === 403) {
                setAccessDenied(err.data?.message || err.message);
                return;
            }
            toast.error(err instanceof Error ? err.message : 'Failed to load delivery');
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => { if (canRead) load(); }, [canRead, load]);

    useEffect(() => {
        if (!canRead) return;
        productsApi.list({ status: 'active' }).then(setProducts).catch(() => undefined);
        // Fleet suggestions for the header edit dialog — same meta the create form reads.
        // The photo rule rides along: the dialog shows the count the chosen type demands.
        deliveriesApi.meta().then((m) => {
            setFleetTypes(m.fleetTypes);
            setPhotoRule({ byFleet: m.photosByFleet ?? {}, fallback: m.minPhotos });
        }).catch(() => undefined);
        // Projects for the header edit's picker — a delivery can be re-pointed at another
        // project of its yard while the run is live (a project, unlike a yard, is a header
        // field). Only active ones are offered; the order's own project is kept regardless
        // so the dialog cannot open on a blank project for a deactivated one.
        projectsApi.list({ status: 'active' }).then(setProjects).catch(() => undefined);
    }, [canRead]);

    // Depends on the order's own yard rather than the :yardId route param — the param is
    // absent on the flat /stock/deliveries/:id route, and the order is the authority.
    const orderYardId = order?.yard?.id ?? null;
    useEffect(() => {
        if (!canRead || !orderYardId) return;
        yardsApi.members(orderYardId)
            .then((rows) => setYardCrew(
                rows.flatMap((m) => (m.user ? [{ id: m.user.id, name: m.user.name }] : []))))
            .catch(() => {
                // An empty picker list reads as "nobody available" rather than a failure,
                // so say which one it is instead of failing silently.
                setYardCrew([]);
                toast.error('Could not load this yard\'s members.');
            });
    }, [canRead, orderYardId]);

    const run = async (key: string, fn: () => Promise<any>, message?: string) => {
        try {
            setBusy(key);
            await fn();
            if (message) toast.success(message);
            await load();
        } catch (err: any) {
            toast.error(err?.data?.message || err?.message || 'Something went wrong');
        } finally {
            setBusy(null);
        }
    };

    /** §12.4 — source paperwork can be attached at any status; there is no gate. */
    const handleDocuments = async (files: FileList | null) => {
        if (!files?.length || !order) return;
        await run('documents',
            () => deliveriesApi.uploadDocuments(order.id, Array.from(files), 'upload'),
            'Document(s) attached');
    };

    /**
     * Every load photo on the order as one ZIP, one folder per truck — what the
     * Transport Manager keeps. Deliberately not routed through `run()`: the download
     * changes nothing about the order, so there is nothing to reload afterwards.
     */
    const downloadPhotos = async () => {
        if (!order) return;
        try {
            setBusy('downloadPhotos');
            await deliveriesApi.downloadPhotos(order.id, order.orderNumber);
        } catch (err) {
            toast.error(err instanceof Error ? err.message : 'Could not download the photos');
        } finally {
            setBusy(null);
        }
    };

    /**
     * Uploads a whole selection, not one file.
     *
     * The endpoint takes a single file per request (upload.single('photo')), so a batch is
     * N requests — but they run three at a time and the order is reloaded ONCE at the end,
     * rather than the previous behaviour of a full refetch after every photo.
     *
     * A failure on one file does not abandon the rest: the yard has patchy signal and
     * losing nine good uploads to one bad one is the worst outcome here.
     *
     * Every file is compressed first. A phone capture is 3–12 MB, and shipping that raw
     * over a yard's mobile signal is where the observed 30–60 s per photo went; at 1600 px
     * and JPEG q0.8 the same shot is a few hundred KB and goes up in about a second, while
     * a rego plate or a gear component is still legible. (§12.1 sizes nothing here — the
     * server still stamps, hashes and audits whatever arrives; this is purely the wire.)
     */
    const uploadPhotos = async (files: File[], vehicleId: number | null, label: string) => {
        if (!order || files.length === 0) return;

        setBusy('photo');
        setUploading({ done: 0, total: files.length, phase: 'preparing', label });

        // One decode at a time: a 12 MP image is ~50 MB once drawn to a canvas, so
        // compressing a whole selection in parallel is how a tab gets killed mid-upload.
        // Ten captured frames compress in a few seconds this way, and the user is told
        // which phase they are waiting on.
        const prepared: File[] = [];
        for (const file of files) {
            // A file the canvas cannot read (an odd codec) goes up as-is rather than
            // disappearing; the server hashes whatever arrives either way.
            prepared.push(await compressImage(file, 1600, 1600, 0.8).catch(() => file));
            setUploading({ done: prepared.length, total: files.length, phase: 'preparing', label });
        }

        const queue = [...prepared];
        const failed: string[] = [];
        let done = 0;
        setUploading({ done: 0, total: files.length, phase: 'uploading', label });

        // Three at a time — enough to hide request latency without opening ten
        // simultaneous uploads from a phone on site.
        const worker = async () => {
            while (queue.length) {
                const file = queue.shift()!;
                try {
                    // Filed against this vehicle's section; null is the order's own truck.
                    await deliveriesApi.uploadPhoto(order.id, file, vehicleId);
                    done += 1;
                    setUploading({ done, total: files.length, phase: 'uploading', label });
                } catch {
                    failed.push(file.name);
                }
            }
        };

        try {
            await Promise.all(
                Array.from({ length: Math.min(3, files.length) }, worker));

            if (failed.length === 0) {
                toast.success(`${done} photo${done === 1 ? '' : 's'} added to ${label}`);
            } else {
                toast.error(
                    `${done} of ${files.length} added. Failed: ${failed.slice(0, 3).join(', ')}` +
                    `${failed.length > 3 ? ` and ${failed.length - 3} more` : ''}. Try those again.`);
            }
        } finally {
            setUploading(null);
            setBusy(null);
            await load();
        }
    };

    /** Both file inputs feed the same uploader; they differ only in what the OS offers. */
    const onPhotoPicked = (vehicleId: number | null, label: string) =>
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const files = Array.from(e.target.files ?? []);
            e.target.value = '';
            void uploadPhotos(files, vehicleId, label);
        };
    /** Shared by the camera and library buttons so they cannot drift apart. */
    const pickerClass = cn(
        'inline-flex items-center gap-2 h-10 px-3 rounded-md border border-input cursor-pointer hover:bg-accent text-sm',
        busy !== null && 'opacity-50 pointer-events-none',
    );

    if (!canRead) return <AccessRestricted />;

    if (accessDenied) {
        return <AccessRestricted accessMessage={accessDenied}
                                 backMessage="Back to Deliveries" navigateTo={listHref} />;
    }

    if (loading || !order) {
        return (
            <div className="space-y-3 animate-pulse p-2 md:p-0">
                {[0, 1, 2].map((i) => <div key={i} className="h-24 bg-gray-300 rounded-md" />)}
            </div>
        );
    }

    const available = order.availableTransitions ?? [];
    const editable = ['draft', 'pending_approval'].includes(order.status);
    const pickable = ['assigned', 'in_progress'].includes(order.status);
    // "Picked" means finished with the line. A partially picked line is an unfinished
    // count — it holds the pick open (see the server's pending_photos guard), so counting
    // it as picked here would promise a completion that the API then refuses.
    const pickedCount = order.lines.filter(
        (l) => l.lineStatus === 'picked' || l.lineStatus === 'shortfall').length;
    /**
     * The material list in product-category sections (the category is the Product
     * Library's field on the product, not something a line carries). Presentation only:
     * the lines, their order within a section, and every rule about them are unchanged.
     */
    const lineGroups = groupLinesByCategory(order.lines);
    /**
     * The photos a given fleet type demands — the dialog's Fleet Type picker has to say
     * what choosing a ute or a 4T does to the requirement before it is saved, and the
     * number is the server's (photoRule), never a second copy of the rule.
     */
    const requiredPhotosFor = (fleetType: string | null | undefined) =>
        photoRule.byFleet[String(fleetType ?? '').trim().toUpperCase()] ?? photoRule.fallback;
    /**
     * The Add Photos card is one section per vehicle: the order's own truck first (photos
     * with no vehicleId, counted against the header's fleet type) and then each live
     * additional vehicle. `required` per section comes from the server's own rule, so the
     * counter and the completion gate can never disagree.
     *
     * A soft-removed vehicle is not a section — nothing is required of it — but its photos
     * are still on the order, so they are listed separately and labelled as evidence.
     */
    const indexedPhotos = order.photos.map((photo, index) => ({ photo, index }));
    const photoSections = [
        {
            key: 'order',
            vehicleId: null as number | null,
            label: [order.fleetType, order.regPlate].filter(Boolean).join(' · ') || 'This truck',
            required: order.minPhotos,
            photos: indexedPhotos.filter(({ photo }) => photo.vehicleId == null),
        },
        ...order.vehicles.map((v) => ({
            key: `v${v.id}`,
            vehicleId: v.id as number | null,
            label: [v.fleetType, v.regPlate].filter(Boolean).join(' · ') || 'Additional vehicle',
            required: v.requiredPhotos,
            photos: indexedPhotos.filter(({ photo }) => photo.vehicleId === v.id),
        })),
    ];
    const removedPhotoSections = order.removedVehicles
        .map((v) => ({
            id: v.id,
            label: [v.fleetType, v.regPlate].filter(Boolean).join(' · ') || 'Removed vehicle',
            photos: indexedPhotos.filter(({ photo }) => photo.vehicleId === v.id),
        }))
        .filter((s) => s.photos.length > 0);
    // Counted across the live sections only: a removed truck's photos are evidence, not
    // progress toward what the run still owes.
    const photosCaptured = photoSections.reduce((n, s) => n + s.photos.length, 0);
    const photosRequired = photoSections.reduce((n, s) => n + s.required, 0);
    const photosComplete = photoSections.every((s) => s.photos.length >= s.required);
    /**
     * Who may change the material list — the mirror of the API's window
     * (deliveryOrder.routes.js, assertLineWriteAllowed).
     *
     * Draft and awaiting approval stay with `update`: the list is still being written and
     * nobody has signed it off. From approval until picking finishes (approved, assigned,
     * in_progress) the list is the AGREED manifest, so only `edit` holders change it — the
     * permission that also lets a manager complete the header. The moment the order reaches
     * `pending_photos` the list is closed for everyone: the load is already photographed,
     * and anything that turns up after that belongs on a new delivery order. The pick crew
     * record what they find on the pick sheet (a count, a shortfall, a partial load); they
     * do not rewrite the sheet they are picking from. Once a line carries a count it is
     * closed here — for everyone — until it is reset on the pick sheet.
     */
    const canUpdateOrder = ability.can(ACTIONS.UPDATE, RESOURCES.STOCK_DELIVERY_ORDER);
    const canEditDelivery = ability.can(ACTIONS.EDIT, RESOURCES.STOCK_DELIVERY_ORDER);
    /**
     * Adding, correcting or taking a truck off the run. Its own permission — the
     * Transport Manager — because each vehicle carries its own fleet type and therefore
     * decides how many load-photo sections the delivery has; `edit` alone (which Yard
     * Managers hold too) must not reshape the photo requirement.
     */
    const canEditVehicles = ability.can(ACTIONS.EDIT_VEHICLES, RESOURCES.STOCK_DELIVERY_ORDER);
    /**
     * Pending Photos -> In Progress: the one way back into picking after the sheet has
     * been pushed on, so a shortfall recorded wrongly (or a line that turned up late) can
     * be corrected. Its own permission — Transport Manager — and not `update`, which the
     * whole pick crew holds. The server filters availableTransitions the same way.
     */
    const canReturnToPicking = ability.can(ACTIONS.RETURN_TO_PICKING, RESOURCES.STOCK_DELIVERY_ORDER);
    /**
     * Number of packs on the shipment: its own read/edit pair, deliberately independent of
     * the delivery's. The counts are advisory — no guard reads them — so they carry no
     * status window: whoever holds `edit` may fill them in at any point in the order's
     * life. `read` decides whether the section is shown at all (the server withholds the
     * values from a caller without it).
     */
    const canReadPacks = ability.can(ACTIONS.READ, RESOURCES.STOCK_DELIVERY_PACKS);
    const canEditPacks = ability.can(ACTIONS.EDIT, RESOURCES.STOCK_DELIVERY_PACKS);
    /** The counts this viewer may see — null when the server withheld them (no read). */
    const packs = order.packs;
    const packsReadOnly = order.status === 'voided';
    const packsValid = packInputs != null &&
        PACK_FIELDS.every((f) => packInputValid(packInputs[f.key]));
    /** Only the five boxes are sent — a Save writes the section as it stands. */
    const savePacks = async () => {
        if (!packInputs) return;
        await run('packs',
            () => deliveriesApi.updatePacks(order.id, packPayload(packInputs)),
            'Pack counts saved');
    };
    const pickEditWindow = ['approved', 'assigned', 'in_progress'].includes(order.status);
    const linesEditable =
        (editable && canUpdateOrder) ||
        (pickEditWindow && canEditDelivery);
    const productOptions = products.map((p) => ({ label: p.name, value: String(p.id), subtitle: p.sku }));
    /**
     * Maker–checker on the approve gate: the creator may approve their own sheet only
     * when they are the yard's sole live manager (abilities.canApproveSelf, decided
     * server-side). Everyone else needs canApprove — yard_manager membership of the
     * owning yard. A viewer with neither never sees the button, matching the API.
     */
    const viewerIsCreator = currentUser != null &&
        Number(order.createdBy?.id) === Number(currentUser.id);
    const canApproveThis = viewerIsCreator
        ? order.abilities.canApproveSelf
        : order.abilities.canApprove;

    const go = (to: DeliveryStatus, extra: Record<string, any> = {}, message?: string) =>
        run(`t:${to}`, () => deliveriesApi.transition(order.id, { to, ...extra }),
            message ?? `Moved to ${to.replace(/_/g, ' ')}`);

    const assignableUsers = yardCrew.filter(
        (u) => !order.assignees.some((a) => a.user?.id === u.id));

    /**
     * Header edit (edit:stock_delivery_order, Manager role): the approving Yard Manager /
     * Transport Manager completes and corrects the truck details — rego and driver can go
     * on the record at any point while the run is live, including after picking has
     * started, because the truck that turns up is not always the one planned. The project
     * is corrected the same way: a load dispatched to the wrong site is re-pointed, not
     * voided and re-raised. Only the terminal states lock it. `canEditDelivery` is declared
     * with the material-list gates above, which read it too.
     */
    const headerEditable = !['entered', 'voided'].includes(order.status);

    /**
     * The run's trucks, in one field: the order's own (the header's fleet/rego) first,
     * then every additional vehicle. This is the "Vehicles" row in the header details.
     */
    const vehicleSummary = [
        [order.fleetType, order.regPlate].filter(Boolean).join(' '),
        ...order.vehicles.map((v) => [v.fleetType, v.regPlate].filter(Boolean).join(' ')),
    ].filter(Boolean).join(' · ');

    /**
     * The project picker's options: this order's yard only, matching the create form —
     * every project belongs to exactly one yard, so a project from another yard cannot be
     * dispatched from this one (the API refuses it too). The order's own project is kept
     * in the list even when it has since been deactivated, so a live order never opens on
     * a blank project; the client's `projects` list only carries active ones.
     */
    const yardProjects = projects.filter((p) => p.yard?.id === order.yard?.id);
    const selectedEditProject = yardProjects.find((p) => String(p.id) === editProjectId) ?? null;
    const editProjectIsCurrent =
        editProjectId !== '' && editProjectId === String(order.project?.id ?? '');
    const editProjectOptions = yardProjects.map((p) => ({
        label: `${p.projectName} (${p.projectNumber})`, value: String(p.id),
    }));
    if (editProjectIsCurrent && !selectedEditProject && order.project) {
        editProjectOptions.unshift({
            label: `${order.project.projectName} (${order.project.projectNumber})`,
            value: String(order.project.id),
        });
    }
    /**
     * Customer and project manager follow the project — read-only, exactly as on create.
     * The API re-derives both from the project it is pointed at, so showing the selected
     * project's own values here is what the save will write, not a separate opinion.
     */
    const editCustomer = selectedEditProject?.customer?.tradingName
        ?? (editProjectIsCurrent ? order.project?.customer?.tradingName ?? order.builder : null)
        ?? '';
    const editProjectManager = selectedEditProject?.projectManager?.name
        ?? (editProjectIsCurrent ? order.supervisor : null)
        ?? '';

    const openEdit = () => {
        setEditProjectId(String(order.project?.id ?? ''));
        setEditFleet(order.fleetType ?? '');
        setEditReg(order.regPlate ?? '');
        setEditDriver(order.driver ?? '');
        setEditShip(order.shipDate ?? '');
        setEditVehicles(order.vehicles.map((v) => ({
            id: v.id,
            fleetType: v.fleetType,
            regPlate: v.regPlate ?? '',
            driver: v.driver ?? '',
        })));
        setEditOpen(true);
    };
    const updateEditVehicle = (index: number, patch: Partial<DeliveryVehicleInput>) =>
        setEditVehicles((prev) => prev.map((v, i) => (i === index ? { ...v, ...patch } : v)));
    const saveEdit = async () => {
        await run('editDetails', () => deliveriesApi.update(order.id, {
            // Sent only when known: the API refuses a PATCH that would leave a delivery
            // with no project, and the dialog will not save without one either.
            ...(editProjectId ? { projectId: Number(editProjectId) } : {}),
            fleetType: editFleet.trim() || null,
            regPlate: editReg.trim() || null,
            driver: editDriver.trim() || null,
            shipDate: editShip || null,
            // The vehicle list rides in the same save, as the whole desired list: the
            // server diffs it (no id = add, id = correct, absent = remove) and audits each
            // change. Only a Transport Manager may send it — everyone else's save leaves
            // the run's trucks exactly as they are.
            ...(canEditVehicles ? {
                vehicles: editVehicles
                    .filter((v) => v.fleetType.trim())
                    .map((v) => ({
                        ...(v.id ? { id: v.id } : {}),
                        fleetType: v.fleetType.trim(),
                        regPlate: (v.regPlate ?? '').trim() || null,
                        driver: (v.driver ?? '').trim() || null,
                    })),
            } : {}),
        }), 'Delivery details updated');
        setEditOpen(false);
    };

    /**
     * Inline line correction — "the sheet said 3.0m × 5, the rack says 2.4m × 4".
     *
     * Item and quantity are one editor because they are one thought: a picker reading a
     * paper sheet corrects what it got wrong, and the server writes a single ITEM EDIT row
     * naming whatever actually moved (unchanged fields are no-ops, so nothing extra lands
     * in the trail). Quantities must stay above zero — taking a line off the sheet is the
     * trash can's job, not a zero.
     */
    const startLineEdit = (line: DeliveryLine) => {
        setEditLineId(line.id);
        setEditProductId(String(line.product?.id ?? ''));
        setEditQuantity(String(line.quantity ?? ''));
    };
    const saveLineEdit = async () => {
        const qty = Number(editQuantity);
        if (editLineId === null || !editProductId || !Number.isInteger(qty) || qty <= 0) return;
        try {
            setBusy('editLine');
            await deliveriesApi.updateLine(order.id, editLineId, {
                productId: Number(editProductId),
                quantity: Number(editQuantity),
            });
            toast.success('Line updated');
            setEditLineId(null);
            await load();
        } catch (err: unknown) {
            // The editor stays open on failure: a product already on the sheet is
            // something the picker fixes by choosing again, not by starting over.
            // `data` is the raw error body, so its message is checked before use.
            const serverMessage = err instanceof ApiError && typeof err.data?.message === 'string'
                ? err.data.message
                : null;
            toast.error(serverMessage
                || (err instanceof Error ? err.message : 'Could not update the line'));
        } finally {
            setBusy(null);
        }
    };

    return (
        <div className="space-y-4 md:space-y-6 p-2 md:p-0 max-w-5xl">
            {/* Header */}
            <div>
                <Button variant="ghost" size="sm" onClick={() => navigate(listHref)} className="mb-2 -ml-2">
                    <ArrowLeft size={16} className="mr-1.5" />Deliveries
                </Button>
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                    <div>
                        <div className="flex items-center gap-3 flex-wrap">
                            <h1 className="text-2xl font-bold font-mono">{order.orderNumber}</h1>
                            <DeliveryStatusPill status={order.status} />
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">
                            {order.project?.projectName}
                            {order.yard ? ` · ${order.yard.yardName}` : ''}
                            {order.shipDate ? ` · ships ${order.shipDate}` : ''}
                        </p>
                    </div>

                    <div className="flex flex-wrap gap-2">
                        {pickable && (
                            <Button asChild variant="outline">
                                <Link to={pickHref}>
                                    <ClipboardList size={16} className="mr-2" />Open pick sheet
                                </Link>
                            </Button>
                        )}
                        {canEditDelivery && headerEditable && (
                            <Button variant="outline" onClick={openEdit} disabled={busy !== null}>
                                {busy === 'editDetails'
                                    ? <Loader2 size={16} className="animate-spin mr-2" />
                                    : <Pencil size={16} className="mr-2" />}
                                Edit details
                            </Button>
                        )}
                        {available.includes('pending_approval') && (
                            <Button onClick={() => go('pending_approval')} disabled={busy !== null}>
                                Submit for approval
                            </Button>
                        )}
                        {available.includes('approved') && canApproveThis && (
                            <Button onClick={() => go('approved')} disabled={busy !== null}>
                                {/* Known Issue #4: on an already-approved order this edge is
                                    UNASSIGN (back to Approved), not an approval — say so. */}
                                {order.status === 'pending_approval'
                                    ? <><CheckCircle2 size={16} className="mr-2" />Approve</>
                                    : <><Undo2 size={16} className="mr-2" />Unassign</>}
                            </Button>
                        )}
                        {/* Reject is the same edge as approve, back to draft with a reason. */}
                        {order.status === 'pending_approval' && order.abilities.canApprove && (
                            <Button variant="outline" onClick={() => { setRejectReason(''); setRejectOpen(true); }}
                                    disabled={busy !== null}>
                                <X size={16} className="mr-2" />Reject
                            </Button>
                        )}
                        {available.includes('pending_photos') && (
                            <Button onClick={() => go('pending_photos')} disabled={busy !== null}>
                                Finish picking
                            </Button>
                        )}
                        {/* The one way back into picking once the sheet has been pushed on:
                            correct a shortfall that was recorded wrongly, or count a line
                            that turned up after the pick. Transport Manager only — the
                            permission is `return_to_picking:stock_delivery_order`, which
                            the pick crew's `update` does not carry. Photos already taken
                            stay on the order; the pick sheet opens again so the crew can
                            fix the count, then Finish picking returns here. */}
                        {order.status === 'pending_photos' && canReturnToPicking && (
                            <Button variant="outline" disabled={busy !== null}
                                    onClick={() => go('in_progress', {}, 'Picking reopened')}>
                                <Undo2 size={16} className="mr-2" />Return to picking
                            </Button>
                        )}
                        {available.includes('completed') && (
                            <Button onClick={() => go('completed')} disabled={busy !== null}>
                                <Check size={16} className="mr-2" />Mark Complete
                            </Button>
                        )}
                        {available.includes('ready_to_enter') && (
                            <Button onClick={() => go('ready_to_enter')} disabled={busy !== null}>
                                Ready to Enter
                            </Button>
                        )}
                        {available.includes('entered') && (
                            <Button onClick={() => { setExternalRef(''); setEnterOpen(true); }} disabled={busy !== null}>
                                <Send size={16} className="mr-2" />Mark Entered
                            </Button>
                        )}
                        {available.includes('voided') && (
                            <Button variant="outline" className="text-destructive"
                                    onClick={() => { setVoidReason(''); setVoidOpen(true); }}
                                    disabled={busy !== null}>
                                <Ban size={16} className="mr-2" />Void
                            </Button>
                        )}
                    </div>
                </div>
                {order.status === 'pending_approval' && viewerIsCreator && !canApproveThis && (
                    <p className="text-sm text-amber-700 flex items-center gap-1.5 mt-2">
                        <AlertTriangle size={14} />
                        Created by you — another Yard Manager needs to approve it before it can go out.
                    </p>
                )}
            </div>

            {order.status === 'voided' && order.voidReason && (
                <Card className="border-red-200 bg-red-50/50">
                    <CardContent className="p-4 text-sm">
                        <p className="font-medium text-red-800">Voided</p>
                        <p className="text-red-700 mt-1">{order.voidReason}</p>
                    </CardContent>
                </Card>
            )}
            {order.status === 'entered' && (
                <Card className="border-emerald-200 bg-emerald-50/50">
                    <CardContent className="p-4 text-sm">
                        <p className="font-medium text-emerald-800">Entered into the external stock system</p>
                        <p className="text-emerald-700 mt-1">
                            Reference <span className="font-mono font-semibold">{order.externalRef}</span>
                        </p>
                    </CardContent>
                </Card>
            )}

            {/* Header details */}
            <Card>
                <CardContent className="p-4 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    {[
                        ['Project', order.project?.projectName],
                        ['Job Number', order.project?.projectNumber],
                        ['Customer', order.builder],
                        ['Project Manager', order.supervisor],
                        ['Fleet', order.fleetType],
                        ['Rego', order.regPlate],
                        ['Driver', order.driver],
                        // Every truck on the run. The Fleet/Rego/Driver rows above describe
                        // the order's own truck; this is the whole list, extras included.
                        ['Vehicles', vehicleSummary],
                        ['Pack date', order.packDate],
                        ['Created by', order.createdBy?.name],
                        ['Approved by', order.approvedBy?.name],
                    ].map(([label, value]) => (
                        <div key={label as string}>
                            <p className="text-muted-foreground text-xs">{label}</p>
                            <p className="font-medium">{(value as string) || '–'}</p>
                        </div>
                    ))}
                </CardContent>
            </Card>

            {/* Crew */}
            <Card>
                <CardContent className="p-4 md:p-6">
                    <h2 className="font-semibold flex items-center gap-2 mb-1">
                        <Users size={18} />Pick crew
                    </h2>
                    <p className="text-sm text-muted-foreground mb-4">
                        Several pickers can work one sheet. A line locks to whoever claims it
                        first; once it is partially picked or flagged short, anyone on the crew
                        can finish or correct it. A picked line stays with the picker who
                        recorded it.
                    </p>

                    {order.abilities.canAssignOthers && !['entered', 'voided'].includes(order.status) && (
                        <div className="flex gap-2 mb-4">                            <Select value={assigneeId} onValueChange={setAssigneeId}>
                                <SelectTrigger>
                                    <SelectValue placeholder={assignableUsers.length
                                        ? 'Add a picker'
                                        : 'No yard members to add'} />
                                </SelectTrigger>
                                <SelectContent>
                                    {assignableUsers.length === 0 ? (
                                        <div className="px-2 py-1.5 text-sm text-muted-foreground">
                                            Nobody left to add from this yard. Manage its members
                                            under Settings → Yards.
                                        </div>
                                    ) : (
                                        assignableUsers.map((u) => (
                                            <SelectItem key={u.id} value={String(u.id)}>{u.name}</SelectItem>
                                        ))
                                    )}
                                </SelectContent>
                            </Select>
                            <Button disabled={!assigneeId || busy !== null}
                                    onClick={() => run('assign', async () => {
                                        await deliveriesApi.addAssignee(order.id, Number(assigneeId));
                                        setAssigneeId('');
                                    }, 'Picker assigned')}>
                                <UserPlus size={16} />
                            </Button>
                        </div>
                    )}

                    {/* UAT B6: without assign-others the dropdown above is hidden — but a
                        Yardsman may still put THEMSELF on the crew (self-only rule). */}
                    {!order.abilities.canAssignOthers && !order.isAssignedToMe &&
                        !['entered', 'voided'].includes(order.status) && currentUser && (
                        <div className="mb-4">
                            <Button variant="outline" disabled={busy !== null}
                                    onClick={() => run('assignSelf', async () => {
                                        await deliveriesApi.addAssignee(order.id, Number(currentUser.id));
                                    }, 'You are on the pick crew')}>
                                <UserPlus size={16} className="mr-2" />Add me to the crew
                            </Button>
                        </div>
                    )}

                    {order.assignees.length === 0 ? (
                        <p className="text-sm text-muted-foreground">Nobody assigned yet.</p>
                    ) : (
                        <div className="flex flex-wrap gap-2">
                            {order.assignees.map((a) => (
                                <span key={a.id}
                                      className="inline-flex items-center gap-1.5 text-sm bg-accent px-3 py-1.5 rounded-md">
                                    {a.user?.name}
                                    {order.abilities.canAssignOthers && !['entered', 'voided'].includes(order.status) && (
                                        <button type="button" aria-label={`Remove ${a.user?.name}`}
                                                className="hover:text-destructive"
                                                onClick={() => run(`rm:${a.id}`,
                                                    () => deliveriesApi.removeAssignee(order.id, a.id),
                                                    'Picker removed')}>
                                            <X size={12} />
                                        </button>
                                    )}
                                </span>
                            ))}
                        </div>
                    )}

                    {order.removedAssignees.length > 0 && (
                        <p className="text-xs text-muted-foreground mt-3">
                            Previously on this sheet: {order.removedAssignees.map((a) => a.user?.name).join(', ')}.
                            Anything they picked stays attributed to them.
                        </p>
                    )}
                </CardContent>
            </Card>

            {/* Number of packs on the shipment — advisory counts, no workflow gate. */}
            {canReadPacks && packs != null && (
                <Card>
                    <CardContent className="p-4 md:p-6">
                        <h2 className="font-semibold flex items-center gap-2 mb-1">
                            <Package size={18} />Number of packs on the shipment
                        </h2>
                        <p className="text-sm text-muted-foreground mb-4">
                            Counts of what went on the trucks. Optional, and for the record
                            only — nothing here changes the delivery or its status.
                            {canEditPacks && !packsReadOnly
                                ? ' Anyone with access can fill them in at any time.'
                                : canEditPacks
                                    ? ' This sheet is voided, so it is a snapshot.'
                                    : ' You do not have permission to change them.'}
                        </p>

                        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                            {PACK_FIELDS.map((f) => (
                                <div key={f.key}>
                                    <Label htmlFor={`pack-${f.key}`} className="text-xs">
                                        {f.label}
                                    </Label>
                                    {canEditPacks && !packsReadOnly && packInputs ? (
                                        <Input id={`pack-${f.key}`} type="number" inputMode="numeric"
                                               min="0" placeholder="Not counted"
                                               className="mt-1 tabular-nums"
                                               value={packInputs[f.key]}
                                               onChange={(e) => setPackInputs((prev) => prev
                                                   ? { ...prev, [f.key]: e.target.value } : prev)} />
                                    ) : (
                                        <p className="font-medium tabular-nums mt-1">
                                            {packs[f.key] ?? '–'}
                                        </p>
                                    )}
                                </div>
                            ))}
                        </div>

                        {canEditPacks && !packsReadOnly && (
                            <div className="flex flex-wrap items-center gap-3 mt-4">
                                <Button disabled={busy !== null || !packsValid}
                                        onClick={() => void savePacks()}>
                                    {busy === 'packs'
                                        ? <Loader2 size={16} className="animate-spin mr-2" />
                                        : <Check size={16} className="mr-2" />}
                                    Save counts
                                </Button>
                                <span className="text-xs text-muted-foreground">
                                    A blank box means not counted yet — it is not the same as zero.
                                </span>
                            </div>
                        )}
                    </CardContent>
                </Card>
            )}

            {/* Material list */}
            <Card>
                <CardContent className="p-4 md:p-6">
                    <div className="flex items-center justify-between gap-3 mb-1">
                        <h2 className="font-semibold">Material list</h2>
                        <span className="text-sm text-muted-foreground">
                            {pickedCount}/{order.lines.length} picked
                        </span>
                    </div>
                    <p className="text-sm text-muted-foreground mb-4">
                        {linesEditable
                            ? (editable
                                ? 'Editable until this goes for approval.'
                                : 'Correct the list while this is being picked — the gear sheet is not '
                                  + 'always right. A line that already carries a count stays locked until '
                                  + 'it is reset on the pick sheet. Adding and correcting closes once '
                                  + 'picking is finished.')
                            : (pickEditWindow
                                ? 'Locked while this is being picked. The pick crew record what they '
                                  + 'find on the pick sheet; a yard manager can still correct the list.'
                                : 'Locked. The list is closed once picking is finished — anything that '
                                  + 'turns up late goes on a new delivery order.')}
                    </p>

                    {linesEditable && (
                        <div className="grid grid-cols-[2fr_1fr_auto] gap-2 mb-4">
                            <Combobox
                                options={productOptions}
                                value={productId || undefined}
                                onChange={(v) => setProductId(v || '')}
                                placeholder="Select a product"
                                searchPlaceholder="Search by name or SKU..."
                                emptyMessage="No matching products"
                            />
                            <Input type="number" inputMode="numeric" min="1" placeholder="Qty"
                                   value={quantity} onChange={(e) => setQuantity(e.target.value)} />
                            <Button disabled={busy !== null || !productId || !quantity}
                                    onClick={() => run('addLine', async () => {
                                        await deliveriesApi.addLine(order.id, {
                                            productId: Number(productId), quantity: Number(quantity),
                                        });
                                        setProductId(''); setQuantity('');
                                    })}>
                                <Plus size={16} />
                            </Button>
                        </div>
                    )}

                    {order.lines.length === 0 ? (
                        <p className="text-sm text-muted-foreground py-4 text-center">No lines yet.</p>
                    ) : (
                        <div className="space-y-3">
                            {lineGroups.map((group) => (
                                <div key={group.category}
                                     className="border border-border rounded-lg overflow-hidden">
                                    {/* The product's own category, from the Product Library
                                        — the sheet reads as sections instead of one long
                                        list, which is what makes it navigable. */}
                                    <div className="flex items-center justify-between gap-2 bg-muted/40 px-3 py-2">
                                        <h3 className="text-sm font-medium">{group.category}</h3>
                                        <span className="text-xs text-muted-foreground tabular-nums">
                                            {group.lines.filter((l) => l.lineStatus === 'picked'
                                                || l.lineStatus === 'shortfall').length}
                                            /{group.lines.length} picked
                                        </span>
                                    </div>
                                    <div className="divide-y divide-border">
                            {group.lines.map((l) => {
                                const editing = editLineId === l.id;
                                // Correcting or dropping a line is only possible while nobody
                                // has acted on it: a picked line is evidence and belongs to
                                // whoever claimed it (API: assertLineUntouched).
                                const canTouchLine = linesEditable && l.lineStatus === 'pending';
                                // Whole numbers only, matching the API: the editor sits outside
                                // a form, so nothing else would stop a decimal being sent.
                                const editedQty = Number(editQuantity);
                                const qtyValid = Number.isInteger(editedQty) && editedQty > 0;
                                return (
                                /* One row per line, but two on a phone: the name wraps at
                                   full width (a scaffold tube name cut off is a guessing
                                   game), and status/count/delete sit on their own line
                                   beneath it rather than fighting the name for width. */
                                <div key={l.id}
                                     className="flex flex-col gap-2 p-3 sm:flex-row sm:items-center sm:gap-3">
                                    <div className="min-w-0 flex-1">
                                        {editing ? (
                                            <Combobox
                                                options={productOptions}
                                                value={editProductId || undefined}
                                                onChange={(v) => setEditProductId(v || '')}
                                                placeholder="Select a product"
                                                searchPlaceholder="Search by name or SKU..."
                                                emptyMessage="No matching products"
                                            />
                                        ) : (
                                            <>
                                                <p className="font-medium sm:truncate">{l.product?.name}</p>
                                                <p className="text-xs text-muted-foreground font-mono">{l.product?.sku}</p>
                                                {l.shortfallReason && (
                                                    <p className="text-xs text-amber-700 mt-0.5 flex items-center gap-1">
                                                        <AlertTriangle size={11} />{l.shortfallReason}
                                                    </p>
                                                )}
                                            </>
                                        )}
                                    </div>
                                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 sm:shrink-0">
                                        {editing ? (
                                            <>
                                                <Input type="number" inputMode="numeric" min="1"
                                                       aria-label="Ordered quantity"
                                                       className="w-24 tabular-nums"
                                                       value={editQuantity}
                                                       onChange={(e) => setEditQuantity(e.target.value)} />
                                                <span className="text-xs text-muted-foreground">
                                                    {l.product?.uom}
                                                </span>
                                                <Button size="icon" aria-label="Save line"
                                                        disabled={busy !== null || !editProductId || !qtyValid}
                                                        onClick={saveLineEdit}>
                                                    {busy === 'editLine'
                                                        ? <Loader2 size={16} className="animate-spin" />
                                                        : <Check size={16} />}
                                                </Button>
                                                <Button variant="ghost" size="icon" aria-label="Cancel edit"
                                                        disabled={busy !== null}
                                                        onClick={() => setEditLineId(null)}>
                                                    <X size={16} />
                                                </Button>
                                            </>
                                        ) : (
                                            <>
                                                <span className={cn(
                                                    'text-xs font-medium px-2 py-1 rounded-md capitalize',
                                                    LINE_STATUS_STYLES[l.lineStatus]
                                                )}>
                                                    {l.lineStatus.replace(/_/g, ' ')}
                                                </span>
                                                <span className="tabular-nums text-sm sm:w-20 sm:text-right">
                                                    {l.qtyPicked ?? '–'} / {l.quantity}
                                                </span>
                                                {l.pickedBy && (
                                                    <span className="text-xs text-muted-foreground sm:w-24 sm:truncate">
                                                        {l.pickedBy.name}
                                                    </span>
                                                )}
                                                {canTouchLine && (
                                                    <>
                                                        <Button variant="ghost" size="icon"
                                                                aria-label={`Edit ${l.product?.name}`}
                                                                disabled={busy !== null}
                                                                onClick={() => startLineEdit(l)}>
                                                            <Pencil size={16} />
                                                        </Button>
                                                        <Button variant="ghost" size="icon" aria-label="Remove line"
                                                                className="text-destructive" disabled={busy !== null}
                                                                onClick={() => run(`rmLine:${l.id}`,
                                                                    () => deliveriesApi.removeLine(order.id, l.id))}>
                                                            <Trash2 size={16} />
                                                        </Button>
                                                    </>
                                                )}
                                            </>
                                        )}
                                    </div>
                                </div>
                                );
                            })}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Photos — one section per vehicle. The order's own truck is the first
                section; every additional vehicle gets its own count and its own
                requirement, and the completion gate checks them one by one. */}
            <Card>
                <CardContent className="p-4 md:p-6">
                    <div className="flex items-center justify-between gap-3 mb-1">
                        <h2 className="font-semibold flex items-center gap-2">
                            <Camera size={18} />Load photos
                        </h2>
                        <div className="flex items-center gap-3">
                            {/* The Transport Manager's copy of the evidence — every
                                truck's photos as one ZIP, folders mirroring these
                                sections. Nothing to download until a photo exists. */}
                            {order.photos.length > 0 && (
                                <Button variant="outline" size="sm"
                                        disabled={busy !== null}
                                        onClick={() => void downloadPhotos()}>
                                    {busy === 'downloadPhotos'
                                        ? <Loader2 size={14} className="animate-spin mr-1.5" />
                                        : <Download size={14} className="mr-1.5" />}
                                    Download all
                                </Button>
                            )}
                            <span className={cn('text-sm font-medium',
                                photosComplete ? 'text-emerald-700' : 'text-muted-foreground')}>
                                {photosCaptured}/{photosRequired}
                            </span>
                        </div>
                    </div>
                    <p className="text-sm text-muted-foreground mb-4">
                        {order.vehicles.length === 0 ? (
                            <>
                                At least {order.minPhotos} photos of the gear loaded on the truck
                                + any components stored hidden + the truck rego plate.
                                {order.fleetType
                                    ? ` The fleet type (${order.fleetType}) sets the count.`
                                    : ` The Transport Manager sets the fleet type before approving —`
                                      + ` UTE needs ${requiredPhotosFor('UTE')}, 4T needs ${requiredPhotosFor('4T')},`
                                      + ` anything else ${requiredPhotosFor(null)}.`}
                            </>
                        ) : (
                            <>
                                Every vehicle loads its own gear, so every vehicle needs its own
                                photos — UTE {requiredPhotosFor('UTE')}, 4T {requiredPhotosFor('4T')},{' '}
                                anything else {requiredPhotosFor(null)}. The delivery cannot be
                                completed until each one below has its count.
                            </>
                        )}
                    </p>

                    {uploading && (
                        <span className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
                            <Loader2 size={16} className="animate-spin" />
                            {uploading.phase === 'preparing' ? 'Preparing' : 'Uploading'}{' '}
                            {uploading.done}/{uploading.total}
                            {uploading.label ? ` to ${uploading.label}` : ''}…
                        </span>
                    )}

                    {photoSections.map((section, si) => {
                        const remaining = Math.max(0, section.required - section.photos.length);
                        const complete = remaining === 0;
                        const canAdd = !['entered', 'voided'].includes(order.status);
                        return (
                            <div key={section.key}
                                 className={cn('rounded-lg border p-3',
                                     si > 0 && 'mt-3',
                                     complete ? 'border-border' : 'border-amber-200 bg-amber-50/30')}>
                                <div className="flex items-center justify-between gap-3">
                                    <p className="font-medium min-w-0 truncate">
                                        {section.label}
                                        {photoSections.length > 1 && si === 0 && (
                                            <span className="text-xs font-normal text-muted-foreground">
                                                {' '}(the order&rsquo;s own truck)
                                            </span>
                                        )}
                                    </p>
                                    <span className={cn('text-sm font-medium tabular-nums shrink-0',
                                        complete ? 'text-emerald-700' : 'text-amber-700')}>
                                        {section.photos.length}/{section.required}
                                    </span>
                                </div>
                                <p className="text-xs text-muted-foreground mt-0.5">
                                    {complete
                                        ? 'Enough photos for this vehicle.'
                                        : `${remaining} more photo${remaining === 1 ? '' : 's'} needed for this vehicle.`}
                                </p>

                                {/*
                                  Two ways in on a touch device, and the attributes are split
                                  across them so the browser never has to arbitrate:

                                  "Take photo" is `capture` with no `multiple` — the rear
                                  camera, one frame, the fastest path and the one that proves
                                  the shot is of THIS truck, today.

                                  "Choose photos" is `multiple` with no `capture` — the photo
                                  library, a whole load selected in one go. That is a
                                  deliberate relaxation of §12.1's camera-only-on-mobile rule
                                  after yard feedback that ten single captures took minutes on
                                  site signal. What survives is the server-side evidence that
                                  was always the enforceable part: authoritative server
                                  timestamp, SHA-256 file hash and the audit row, so a re-used
                                  frame is still detectable after the fact.

                                  Each section's inputs carry that section's vehicleId, so a
                                  frame is filed against the truck it was taken from.
                                */}
                                {canAdd && (
                                    <div className="flex flex-wrap items-center gap-2 mt-3">
                                        {isTouchDevice && (
                                            <label className={pickerClass}>
                                                <Camera size={16} />Take photo
                                                <input type="file" accept="image/*" capture="environment"
                                                       className="hidden"
                                                       onChange={onPhotoPicked(section.vehicleId, section.label)} />
                                            </label>
                                        )}
                                        <label className={pickerClass}>
                                            <Images size={16} />{isTouchDevice ? 'Choose photos' : 'Add photos'}
                                            <input type="file" accept="image/*" multiple
                                                   className="hidden"
                                                   onChange={onPhotoPicked(section.vehicleId, section.label)} />
                                        </label>
                                    </div>
                                )}

                                {section.photos.length > 0 && (
                                    <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 mt-3">
                                        {section.photos.map(({ photo, index }) => (
                                            <button key={photo.id} type="button" onClick={() => setPhotoIndex(index)}
                                                    aria-label={`Open load photo ${index + 1} of ${order.photos.length} for ${section.label}`}
                                                    className="group relative rounded-md overflow-hidden border border-border focus:outline-none focus:ring-2 focus:ring-ring">
                                                <img src={photo.url} alt={`Load on ${section.label}`}
                                                     className="aspect-square w-full object-cover transition-transform group-hover:scale-105" />
                                                <span className="absolute inset-0 bg-black/0 group-hover:bg-black/25 transition-colors flex items-center justify-center">
                                                    <Expand size={18}
                                                            className="text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                                                </span>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        );
                    })}

                    {/* A truck that came off the run keeps its photos — they are evidence
                        (§12.1, append-only) — so they are still shown. They are simply no
                        longer a section: nothing is required of them. */}
                    {removedPhotoSections.map((section) => (
                        <div key={`removed-${section.id}`}
                             className="mt-3 rounded-lg border border-dashed border-border bg-muted/30 p-3">
                            <div className="flex flex-wrap items-center justify-between gap-2">
                                <p className="text-sm text-muted-foreground">
                                    Removed vehicle · {section.label}
                                </p>
                                <span className="text-xs text-muted-foreground tabular-nums">
                                    {section.photos.length} photo{section.photos.length === 1 ? '' : 's'} kept as evidence
                                </span>
                            </div>
                            <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 mt-3">
                                {section.photos.map(({ photo, index }) => (
                                    <button key={photo.id} type="button" onClick={() => setPhotoIndex(index)}
                                            aria-label={`Open load photo ${index + 1} of ${order.photos.length} for removed vehicle ${section.label}`}
                                            className="group relative rounded-md overflow-hidden border border-border opacity-70 focus:outline-none focus:ring-2 focus:ring-ring">
                                        <img src={photo.url} alt={`Load on ${section.label}`}
                                             className="aspect-square w-full object-cover" />
                                    </button>
                                ))}
                            </div>
                        </div>
                    ))}
                </CardContent>
            </Card>

            {/* §12.4 — source paperwork: PDFs/photos stored as the source of record,
                incl. the sheet the AI read. No status gate; links are re-signed per read. */}
            <Card>
                <CardContent className="p-4 md:p-6">
                    <div className="flex items-center justify-between gap-2 mb-1">
                        <h2 className="font-semibold flex items-center gap-2">
                            <Paperclip size={18} />Source documents
                        </h2>
                        {canAttachDocs && (
                            <>
                                <input
                                    ref={docInput}
                                    type="file"
                                    accept="application/pdf,image/*"
                                    multiple
                                    className="hidden"
                                    onChange={(e) => {
                                        handleDocuments(e.target.files);
                                        e.target.value = '';
                                    }}
                                />
                                <Button variant="outline" size="sm"
                                        disabled={busy !== null}
                                        onClick={() => docInput.current?.click()}>
                                    {busy === 'documents'
                                        ? <Loader2 size={14} className="animate-spin mr-1.5" />
                                        : <Plus size={14} className="mr-1.5" />}
                                    Add documents
                                </Button>
                            </>
                        )}
                    </div>
                    <p className="text-sm text-muted-foreground mb-3">
                        The paperwork behind this delivery, kept so the original is never lost.
                    </p>
                    {order.documents.length === 0 ? (
                        <p className="text-sm text-muted-foreground">No source documents yet.</p>
                    ) : (
                        <div className="divide-y divide-border border border-border rounded-lg">
                            {order.documents.map((d) => (
                                <div key={d.id} className="flex items-center pr-1">
                                    <a href={d.url} target="_blank" rel="noreferrer"
                                       className="flex items-center gap-3 p-3 hover:bg-accent/40 transition-colors flex-1 min-w-0">
                                        <span className="w-10 h-10 rounded-md border border-border bg-muted/40 flex items-center justify-center shrink-0">
                                            <FileText size={16} className="text-muted-foreground" />
                                        </span>
                                        <span className="min-w-0 flex-1">
                                            <span className="block text-sm font-medium truncate">{d.filename}</span>
                                            <span className="block text-xs text-muted-foreground">
                                                {d.uploadedBy?.name} · {new Date(d.createdAt).toLocaleString()}
                                            </span>
                                        </span>
                                        {d.source === 'ai_parse' && (
                                            <span className="text-[10px] font-medium text-blue-700 bg-blue-50 rounded px-1.5 py-0.5 shrink-0">
                                                AI sheet
                                            </span>
                                        )}
                                    </a>
                                    {canDeleteDocs && (
                                        <Button variant="ghost" size="icon"
                                                aria-label={`Remove ${d.filename}`}
                                                className="text-destructive shrink-0"
                                                disabled={busy !== null}
                                                onClick={() => setDocToDelete(d)}>
                                            <Trash2 size={16} />
                                        </Button>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Comments */}
            <Card>
                <CardContent className="p-4 md:p-6">
                    <h2 className="font-semibold flex items-center gap-2 mb-3">
                        <MessageSquare size={18} />Comments
                    </h2>
                    <div className="flex flex-col sm:flex-row gap-2 mb-4">
                        <Textarea rows={2} placeholder="Add context…" value={comment}
                                  onChange={(e) => setComment(e.target.value)} />
                        <Button disabled={busy !== null || !comment.trim()}
                                onClick={() => run('comment', async () => {
                                    await deliveriesApi.comment(order.id, comment.trim());
                                    setComment('');
                                })}>
                            Post
                        </Button>
                    </div>
                    {order.comments.length === 0 ? (
                        <p className="text-sm text-muted-foreground">No comments.</p>
                    ) : (
                        <div className="space-y-3">
                            {order.comments.map((c) => (
                                <div key={c.id} className="text-sm">
                                    <p className="font-medium">
                                        {c.user?.name}
                                        <span className="text-xs text-muted-foreground font-normal ml-2">
                                            {new Date(c.createdAt).toLocaleString()}
                                        </span>
                                    </p>
                                    <p className="text-muted-foreground whitespace-pre-wrap">{c.body}</p>
                                </div>
                            ))}
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Audit */}
            <Card>
                <CardContent className="p-4 md:p-6">
                    <h2 className="font-semibold mb-3">Audit trail</h2>
                    <div className="space-y-2 text-sm">
                        {order.history.map((h) => {
                            const reason = h.reason;
                            // Evidence rows — material-line adds/removals, crew changes,
                            // claims, picks and shortfalls, photos, comments — carry
                            // fromStatus === toStatus: the status did not change, the
                            // reason text says what actually happened (§12.3). Status
                            // moves and the creation row render as before.
                            const evidence = h.fromStatus !== null && h.fromStatus === h.toStatus;
                            const sep = evidence && reason ? reason.indexOf(' — ') : -1;
                            const kind = reason && sep > 0 ? reason.slice(0, sep) : null;
                            const detail = reason && sep > 0
                                ? reason.slice(sep + 3)
                                : (evidence && reason ? reason : null);
                            return (
                                <div key={h.id} className="flex flex-wrap items-baseline gap-x-2">
                                    <span className="text-muted-foreground text-xs tabular-nums">
                                        {new Date(h.createdAt).toLocaleString()}
                                    </span>
                                    {evidence ? (
                                        <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground bg-muted rounded px-1.5 py-0.5">
                                            {kind ?? 'note'}
                                        </span>
                                    ) : (
                                        <span>
                                            {h.fromStatus
                                                ? `${String(h.fromStatus).replace(/_/g, ' ')} → ${String(h.toStatus).replace(/_/g, ' ')}`
                                                : `created as ${String(h.toStatus).replace(/_/g, ' ')}`}
                                        </span>
                                    )}
                                    <span className="text-muted-foreground">· {h.actorName}</span>
                                    {detail && <span className="text-muted-foreground w-full">{detail}</span>}
                                    {!evidence && h.reason && (
                                        <span className="text-muted-foreground w-full">{h.reason}</span>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </CardContent>
            </Card>

            {/* Reject */}
            <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
                <DialogContent className="sm:max-w-[480px]">
                    <DialogHeader>
                        <DialogTitle>Reject {order.orderNumber}</DialogTitle>
                        <DialogDescription>
                            Sends it back to Draft so the creator can fix it. The reason is recorded.
                        </DialogDescription>
                    </DialogHeader>
                    <div>
                        <Label htmlFor="reject-reason">Reason *</Label>
                        <Textarea id="reject-reason" rows={3} value={rejectReason}
                                  onChange={(e) => setRejectReason(e.target.value)} />
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setRejectOpen(false)}>Cancel</Button>
                        <Button disabled={busy !== null || !rejectReason.trim()}
                                onClick={async () => {
                                    await go('draft', { reason: rejectReason.trim() });
                                    setRejectOpen(false);
                                }}>
                            Reject
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Void */}
            <Dialog open={voidOpen} onOpenChange={setVoidOpen}>
                <DialogContent className="sm:max-w-[520px]">
                    <DialogHeader>
                        <DialogTitle>Void {order.orderNumber}</DialogTitle>
                        <DialogDescription>
                            Lines, photos and who picked what are snapshotted into the audit trail first.
                        </DialogDescription>
                    </DialogHeader>
                    <div>
                        <Label htmlFor="void-reason">Reason * (at least 20 characters)</Label>
                        <Textarea id="void-reason" rows={3} value={voidReason}
                                  onChange={(e) => setVoidReason(e.target.value)} />
                        <p className="text-xs text-muted-foreground mt-1">{voidReason.trim().length}/20</p>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setVoidOpen(false)}>Cancel</Button>
                        <Button variant="destructive"
                                disabled={busy !== null || voidReason.trim().length < 20}
                                onClick={async () => {
                                    await go('voided', { reason: voidReason.trim() });
                                    setVoidOpen(false);
                                }}>
                            <Ban size={16} className="mr-2" />Void
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Entered */}
            <Dialog open={enterOpen} onOpenChange={setEnterOpen}>
                <DialogContent className="sm:max-w-[480px]">
                    <DialogHeader>
                        <DialogTitle>Mark {order.orderNumber} Entered</DialogTitle>
                        <DialogDescription>
                            Terminal — the reference cannot be edited afterwards.
                        </DialogDescription>
                    </DialogHeader>
                    <div>
                        <Label htmlFor="ext">External Reference Number *</Label>
                        <Input id="ext" value={externalRef} maxLength={16} className="font-mono"
                               onChange={(e) => setExternalRef(e.target.value)} />
                        <p className="text-xs text-muted-foreground mt-1">Max 16 characters.</p>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setEnterOpen(false)}>Cancel</Button>
                        <Button disabled={busy !== null || !externalRef.trim()}
                                onClick={async () => {
                                    await go('entered', { externalRef: externalRef.trim() });
                                    setEnterOpen(false);
                                }}>
                            <Send size={16} className="mr-2" />Mark Entered
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Edit details — the truck is allocated: rego and driver go on the record at
                any point while the run is live, before it is entered externally; the
                project is corrected in the same window */}
            <Dialog open={editOpen} onOpenChange={setEditOpen}>
                <DialogContent className="sm:max-w-[520px]">
                    <DialogHeader>
                        <DialogTitle>Edit {order.orderNumber}</DialogTitle>
                        <DialogDescription>
                            Complete the details of the allocated run — project, rego, driver,
                            fleet and the additional vehicles can be corrected at any point
                            while the delivery is live, including once picking has started.
                            Moving the project carries its customer and project manager with
                            it. Changes are recorded in the audit trail.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <Label>Project *</Label>
                            <Combobox
                                options={editProjectOptions}
                                value={editProjectId || undefined}
                                onChange={(v) => setEditProjectId(v || '')}
                                placeholder="Select a project"
                                searchPlaceholder="Search projects..."
                                emptyMessage="No projects for this yard"
                            />
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                                <Label htmlFor="edit-customer">Customer</Label>
                                <Input id="edit-customer" value={editCustomer} disabled
                                       placeholder="From the selected project" />
                            </div>
                            <div>
                                <Label htmlFor="edit-pm">Project Manager</Label>
                                <Input id="edit-pm" value={editProjectManager} disabled
                                       placeholder="From the selected project" />
                            </div>
                        </div>
                        <div>
                            <Label htmlFor="edit-fleet">Fleet Type</Label>
                            <Select value={editFleet} onValueChange={setEditFleet}>
                                <SelectTrigger id="edit-fleet">
                                    <SelectValue placeholder="Select" />
                                </SelectTrigger>
                                <SelectContent>
                                    {fleetTypes.map((f) => (
                                        <SelectItem key={f} value={f}>{f}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            {/* The pickup point for Ops' rule: the count the load photos
                                are checked against follows whatever is chosen here. */}
                            <p className="text-xs text-muted-foreground mt-1">
                                Load photos required: {requiredPhotosFor(editFleet)}
                            </p>
                        </div>
                        <div>
                            <Label htmlFor="edit-rego">Rego / Plate</Label>
                            <Input id="edit-rego" value={editReg}
                                   onChange={(e) => setEditReg(e.target.value)}
                                   placeholder="e.g. ABC-123" className="font-mono uppercase"
                                   maxLength={12} />
                        </div>
                        <div>
                            <Label htmlFor="edit-driver">Driver</Label>
                            <Input id="edit-driver" value={editDriver}
                                   onChange={(e) => setEditDriver(e.target.value)}
                                   placeholder="Driver's full name" />
                        </div>
                        <div>
                            <Label htmlFor="edit-ship">Ship Date *</Label>
                            <Input id="edit-ship" type="date" value={editShip}
                                   onChange={(e) => setEditShip(e.target.value)} />
                        </div>

                        {/* Additional vehicles — the other trucks on the run. Each carries its
                            own fleet type, and that is what sets its load-photo count, so the
                            list is the Transport Manager's (edit_vehicles). A Yard Manager
                            sees it read-only. */}
                        <div className="border-t border-border pt-4">
                            <Label>Additional vehicles</Label>
                            {canEditVehicles ? (
                                <>
                                    {editVehicles.length === 0 ? (
                                        <p className="text-xs text-muted-foreground mt-1">
                                            One truck is on the sheet. Add another if the load
                                            does not fit it — each vehicle gets its own load
                                            photos to capture.
                                        </p>
                                    ) : (
                                        <div className="space-y-2 mt-2">
                                            {editVehicles.map((v, i) => (
                                                <div key={v.id ?? `new-${i}`}
                                                     className="grid grid-cols-[1fr_1fr_1fr_auto] gap-2 items-center">
                                                    <Select value={v.fleetType || undefined}
                                                            onValueChange={(val) => updateEditVehicle(i, { fleetType: val })}>
                                                        <SelectTrigger aria-label={`Vehicle ${i + 1} fleet type`}>
                                                            <SelectValue placeholder="Fleet" />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            {/* The order's own fleet type is a
                                                                suggestion, not a constraint —
                                                                free text before, kept that way. */}
                                                            {Array.from(new Set([
                                                                ...fleetTypes,
                                                                ...(v.fleetType ? [v.fleetType] : []),
                                                            ])).map((f) => (
                                                                <SelectItem key={f} value={f}>{f}</SelectItem>
                                                            ))}
                                                        </SelectContent>
                                                    </Select>
                                                    <Input aria-label={`Vehicle ${i + 1} rego`}
                                                           placeholder="Rego" value={v.regPlate ?? ''}
                                                           className="font-mono uppercase" maxLength={12}
                                                           onChange={(e) => updateEditVehicle(i, { regPlate: e.target.value })} />
                                                    <Input aria-label={`Vehicle ${i + 1} driver`}
                                                           placeholder="Driver" value={v.driver ?? ''}
                                                           onChange={(e) => updateEditVehicle(i, { driver: e.target.value })} />
                                                    <Button type="button" variant="ghost" size="icon"
                                                            aria-label={`Remove vehicle ${i + 1}`}
                                                            className="text-destructive"
                                                            onClick={() => setEditVehicles(
                                                                (prev) => prev.filter((_, j) => j !== i))}>
                                                        <Trash2 size={16} />
                                                    </Button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    <Button type="button" variant="outline" size="sm" className="mt-2"
                                            onClick={() => setEditVehicles(
                                                (prev) => [...prev, { fleetType: '', regPlate: '', driver: '' }])}>
                                        <Plus size={14} className="mr-1.5" />Add vehicle
                                    </Button>
                                    <p className="text-xs text-muted-foreground mt-2">
                                        Load photos required per vehicle: UTE {requiredPhotosFor('UTE')},
                                        {' '}4T {requiredPhotosFor('4T')}, anything else{' '}
                                        {requiredPhotosFor(null)}.
                                    </p>
                                </>
                            ) : order.vehicles.length === 0 ? (
                                <p className="text-xs text-muted-foreground mt-1">
                                    None — one truck is on the sheet. Only a Transport Manager can
                                    add another.
                                </p>
                            ) : (
                                <ul className="mt-1 space-y-1 text-sm text-muted-foreground">
                                    {order.vehicles.map((v) => (
                                        <li key={v.id}>
                                            {[v.fleetType, v.regPlate, v.driver].filter(Boolean).join(' · ')}
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
                        {/* Required, like it is on create: the server refuses a save that
                            would leave the order without a date or a project. */}
                        <Button disabled={busy !== null || !editShip || !editProjectId} onClick={saveEdit}>
                            {busy === 'editDetails' && <Loader2 size={16} className="animate-spin mr-2" />}
                            Save details
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Remove a source document — a wrongly attached file can be corrected (§12.4) */}
            <Dialog open={docToDelete !== null} onOpenChange={(o) => { if (!o) setDocToDelete(null); }}>
                <DialogContent className="sm:max-w-[480px]">
                    <DialogHeader>
                        <DialogTitle>Remove source document?</DialogTitle>
                        <DialogDescription>
                            Removes {docToDelete?.filename} from {order.orderNumber}. The removal is
                            snapshotted into the audit trail, so who deleted it and when is never lost.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" disabled={busy !== null}
                                onClick={() => setDocToDelete(null)}>Cancel</Button>
                        <Button variant="destructive" disabled={busy !== null}
                                onClick={async () => {
                                    if (!docToDelete) return;
                                    const target = docToDelete;
                                    await run('delDoc',
                                        () => deliveriesApi.deleteDocument(order.id, target.id),
                                        'Document removed');
                                    setDocToDelete(null);
                                }}>
                            {busy === 'delDoc' && <Loader2 size={16} className="animate-spin mr-2" />}
                            Remove document
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <ImageLightbox
                alt="Load photo"
                images={order.photos.map((p) => ({
                    id: p.id,
                    url: p.url,
                    label: p.capturedBy?.name ? `Taken by ${p.capturedBy.name}` : null,
                    // serverTimestamp, not deviceTimestamp — §12.1 records the device clock
                    // but does not trust it.
                    caption: new Date(p.serverTimestamp).toLocaleString(),
                }))}
                index={photoIndex}
                onIndexChange={setPhotoIndex}
            />
        </div>
    );
}
